import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ThemeMode, FontSizeScale } from '../theme/types';

const THEME_KEY = '@cynosure/theme_mode';
const FONT_SIZE_KEY = '@cynosure/font_size_scale';

const VALID_FONT_SCALES: FontSizeScale[] = ['small', 'default', 'large', 'xlarge'];

interface ThemeState {
  mode: ThemeMode;
  fontSizeScale: FontSizeScale;
  isInitialized: boolean;
  setMode: (mode: ThemeMode) => Promise<void>;
  setFontSizeScale: (scale: FontSizeScale) => Promise<void>;
  initialize: () => Promise<void>;
}

export const useThemeStore = create<ThemeState>((set) => ({
  // Follow the device until the user picks Light or Dark in Personalize. A
  // stored choice still wins (initialize), so nobody who chose is changed.
  mode: 'system',
  fontSizeScale: 'default',
  isInitialized: false,

  setMode: async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_KEY, mode);
      set({ mode });
    } catch (error) {
      console.error('Failed to save theme mode:', error);
    }
  },

  setFontSizeScale: async (scale: FontSizeScale) => {
    try {
      await AsyncStorage.setItem(FONT_SIZE_KEY, scale);
      set({ fontSizeScale: scale });
    } catch (error) {
      console.error('Failed to save font size scale:', error);
    }
  },

  initialize: async () => {
    try {
      const [storedTheme, storedFontSize] = await Promise.all([
        AsyncStorage.getItem(THEME_KEY),
        AsyncStorage.getItem(FONT_SIZE_KEY),
      ]);
      const updates: Partial<ThemeState> = { isInitialized: true };
      if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system') {
        updates.mode = storedTheme;
      }
      if (storedFontSize && VALID_FONT_SCALES.includes(storedFontSize as FontSizeScale)) {
        updates.fontSizeScale = storedFontSize as FontSizeScale;
      }
      set(updates);
    } catch {
      set({ isInitialized: true });
    }
  },
}));
